import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { UserResponse } from '../../models/core/user';

interface UserStore {
  // User profile data
  profile: UserResponse | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setProfile: (profile: UserResponse) => void;
  updateProfile: (updates: Partial<UserResponse>) => void;
  clearProfile: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const UserStore = create<UserStore>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        profile: null,
        isLoading: false,
        error: null,
        
        // Set full user profile
        setProfile: (profile: UserResponse) => {
          set({ 
            profile,
            error: null 
          });
        },
        
        // Update profile partially
        updateProfile: (updates: Partial<UserResponse>) => {
          set((state) => ({
            profile: state.profile ? { ...state.profile, ...updates } : null,
            error: null
          }));
        },
        
        // Clear profile
        clearProfile: () => {
          set({ 
            profile: null,
            error: null 
          });
        },
        
        // Set loading state
        setLoading: (loading: boolean) => {
          set({ isLoading: loading });
        },
        
        // Set error
        setError: (error: string | null) => {
          set({ error });
        },
      }),
      {
        name: 'user-profile-storage',
        partialize: (state) => ({ profile: state.profile }),
      }
    ),
    { name: 'UserStore' }
  )
);

// Convenience hooks
export const useUserProfile = () => UserStore((state) => state.profile);
export const useUserActions = () => UserStore(useShallow((state) => ({
  setProfile: state.setProfile,
  updateProfile: state.updateProfile,
  clearProfile: state.clearProfile,
})));
export const useUserIsLoading = () => UserStore((state) => state.isLoading);